import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';

import { Button } from '@components/commons';
import Filter from '@components/Filter';
import { GroupBy } from '@types';
import { IntlContext } from '@utils/useIntl';

export type ColorFilter = 'b&w' | 'color';

interface GalleryFiltersProps {
  colorFilter: ColorFilter;
  groupBy: GroupBy;
  hasColorAndBnw: boolean;
  hasMultipleLocations: boolean;
  hasMultipleYears: boolean;
  setColorFilter?: (value: ColorFilter) => void;
  setGroupBy: (value: GroupBy) => void;
}

const colorButtonClassNames =
  'w-5 h-5 p-0.5 rounded-full border shrink-0 lowercase cursor-pointer';
const colorButtonInsideClassNames =
  'w-full h-full bg-linear-to-br rounded-full';

const GalleryFilters = ({
  colorFilter,
  groupBy,
  hasColorAndBnw,
  hasMultipleLocations,
  hasMultipleYears,
  setColorFilter,
  setGroupBy,
}: GalleryFiltersProps) => {
  const { translate } = useContext(IntlContext);

  const [openGroupBy, setOpenGroupBy] = useState(false);
  const [openColors, setOpenColors] = useState(false);

  const switchColorFilter = (value: ColorFilter) => {
    if (value === colorFilter) {
      setColorFilter(undefined);
    } else {
      setColorFilter(value);
    }
  };

  useEffect(() => {
    setOpenGroupBy(false);
  }, [groupBy]);

  useEffect(() => {
    setOpenColors(false);
  }, [colorFilter]);

  if (!hasColorAndBnw && !hasMultipleLocations && !hasMultipleYears)
    return null;

  return (
    <div className="flex grow max-tablet:items-start justify-center tablet:justify-end gap-8">
      {(hasMultipleLocations || hasMultipleYears) && (
        <Filter
          className={openColors ? 'max-tablet:hidden' : undefined}
          title={translate('gallery.groupBy')}
          isOpen={openGroupBy}
          onOpen={() => {
            setOpenGroupBy(true);
            setOpenColors(false);
          }}
          onClose={() => setOpenGroupBy(false)}
        >
          {hasMultipleLocations && (
            <Button
              selected={groupBy === 'location'}
              onClick={() => setGroupBy('location')}
            >
              {translate('gallery.groupBy.locations')}
            </Button>
          )}
          {hasMultipleYears && (
            <Button
              selected={groupBy === 'year'}
              onClick={() => setGroupBy('year')}
            >
              {translate('gallery.groupBy.years')}
            </Button>
          )}
          <Button selected={!groupBy} onClick={() => setGroupBy(undefined)}>
            {translate('gallery.groupBy.all')}
          </Button>
        </Filter>
      )}
      {hasColorAndBnw && (
        <Filter
          className={openGroupBy ? 'max-tablet:hidden' : undefined}
          title={translate('gallery.colors')}
          isOpen={openColors}
          onOpen={() => {
            setOpenColors(true);
            setOpenGroupBy(false);
          }}
          onClose={() => setOpenColors(false)}
        >
          <button
            title={translate('gallery.colors.bnw')}
            className={classNames(
              colorButtonClassNames,
              colorFilter === 'b&w' ? 'border-accent' : 'border-gray',
            )}
            onClick={() => switchColorFilter('b&w')}
          >
            <div
              className={classNames(
                colorButtonInsideClassNames,
                'from-white to-black',
              )}
            />
          </button>
          <button
            title={translate('gallery.colors.color')}
            className={classNames(
              colorButtonClassNames,
              colorFilter === 'color' ? 'border-accent' : 'border-gray',
            )}
            onClick={() => switchColorFilter('color')}
          >
            <div
              className={classNames(
                colorButtonInsideClassNames,
                'from-accent to-accent2',
              )}
            />
          </button>
        </Filter>
      )}
    </div>
  );
};

export default GalleryFilters;
